// ---- Cinematic shot solver (plan 26) ----
// Turns a shot's camera spec into a per-frame desiredPos/desiredTarget over the
// LIVE actor anchors (actors move mid-shot), eased or hard-cut onto the given
// camera. Deliberately keeps its OWN instance-local eased vectors — camera.ts's
// module-private _camPos/_camTarget belong to the game's activity cameras, and
// the studio must run this solver with no game camera state at all. Same proven
// idiom though: eased position + eased lookAt (research + repo agree it reads
// better than quaternion keyframes); the `path` escape hatch rides
// CatmullRomCurve3.getPointAt (arc-length param ⇒ constant-speed dollies).
//
// Every named-shot framing default lives in SHOT_DEFAULTS — THE tuning surface.
// Don't scatter magic numbers into the branches.

import * as THREE from "three";
import type { CineCamera } from "../content/schemas.ts";

export const SHOT_DEFAULTS = {
  easeTime: 0.35, // seconds to ~63% — scaled so 0.35 matches the talk-camera feel
  lookY: 1.2, // generic "at the person" look height above feet
  faceY: 1.55, // "at the face" look height above feet
  birdseye: { height: 24, drift: 0, poleOffset: 0.22 }, // poleOffset × height keeps lookAt off the straight-down pole
  wide: { dist: 12, azimuth: 0.6, height: 4 },
  twoShot: { base: 4, sepMul: 1.1, height: 3, lookY: 1.4 }, // sideDist = sep·sepMul + base + dist
  overShoulder: { back: 1.7, side: 0.6, eye: 1.85 },
  closeUp: { ring: 2.2, azimuth: 0.5, camY: 1.6 },
  follow: { dist: 6, height: 2.2, azimuth: 0 },
  orbit: { dist: 8, height: 3, degPerSec: 12 },
};

/** A live actor anchor: feet position + facing. */
export type ActorAnchor = { x: number; y: number; z: number; ry: number };

export type AnchorResolver = {
  actor(id: string): ActorAnchor | null;
  /** Terrain height for fixed [x,z] camera refs. */
  groundY(x: number, z: number): number;
};

export type CineCameraRig = {
  /** Drive the camera for the active shot. `cut` seeds (no ease frame);
   *  `duration` is the owning shot's duration (path shots require it). */
  apply(spec: CineCamera, shotT: number, duration: number | undefined, dt: number, cut: boolean): void;
};

const UP = new THREE.Vector3(0, 1, 0);

export function makeCineCamera(camera: THREE.PerspectiveCamera, res: AnchorResolver): CineCameraRig {
  const camPos = new THREE.Vector3();
  const camTarget = new THREE.Vector3();
  const desiredPos = new THREE.Vector3();
  const desiredTarget = new THREE.Vector3();
  const p = new THREE.Vector3(); // scratch: resolved anchor point
  const q = new THREE.Vector3(); // scratch: second point / direction work
  const prevPoint = new THREE.Vector3(); // follow-shot travel derivation

  // Per-shot-entry state, reset whenever the spec object changes (one spec
  // object per shot in the doc, stable identity).
  let lastSpec: CineCamera | null = null;
  let orbitAngle = 0;
  let driftT = 0;
  let pickedSide = 1;
  let havePrev = false;
  let haveDir = false;
  const lastDir = new THREE.Vector3(); // follow: last real travel direction
  let curve: THREE.CatmullRomCurve3 | null = null;

  /** Unit x/z direction a→b + their separation (ε-guarded when coincident).
   *  Reused scratch — consume synchronously, like the vectors above. */
  const _dir = { dx: 0, dz: 0, sep: 0 };
  function unitDir(a: { x: number; z: number }, b: { x: number; z: number }): typeof _dir {
    _dir.dx = b.x - a.x;
    _dir.dz = b.z - a.z;
    _dir.sep = Math.hypot(_dir.dx, _dir.dz) || 1e-6;
    _dir.dx /= _dir.sep;
    _dir.dz /= _dir.sep;
    return _dir;
  }

  /** Resolve an ActorRef (id | id[] | [x,z]) into `out` (feet height). Returns
   *  the single actor's anchor when the ref names exactly one. */
  function point(ref: unknown, out: THREE.Vector3): ActorAnchor | null {
    if (typeof ref === "string") {
      const a = res.actor(ref);
      if (a) out.set(a.x, a.y, a.z);
      return a;
    }
    if (Array.isArray(ref) && typeof ref[0] === "string") {
      out.set(0, 0, 0);
      let n = 0;
      for (const id of ref as string[]) {
        const a = res.actor(id);
        if (!a) continue;
        out.x += a.x;
        out.y += a.y;
        out.z += a.z;
        n++;
      }
      if (n) out.multiplyScalar(1 / n);
      return null;
    }
    if (Array.isArray(ref) && typeof ref[0] === "number") {
      if (ref.length >= 3) out.set(ref[0] as number, ref[1] as number, ref[2] as number);
      else out.set(ref[0] as number, res.groundY(ref[0] as number, ref[1] as number), ref[1] as number);
    }
    return null;
  }

  function enterShot(spec: CineCamera): void {
    lastSpec = spec;
    orbitAngle = 0;
    driftT = 0;
    havePrev = false;
    haveDir = false;
    curve = null;
    if (spec.shot === "path") {
      curve = new THREE.CatmullRomCurve3(
        spec.points.map(([x, y, z]) => new THREE.Vector3(x, y, z)),
        spec.closed ?? false,
      );
    }
    if (spec.shot === "twoShot" || spec.shot === "overShoulder") {
      // Pick the framing side ONCE from wherever the camera already is (the
      // talk-camera trick — shortest swing, never through the subjects).
      const a = res.actor(spec.shot === "twoShot" ? spec.a : spec.from);
      const b = res.actor(spec.shot === "twoShot" ? spec.b : spec.to);
      if (a && b) {
        const mx = (a.x + b.x) / 2;
        const mz = (a.z + b.z) / 2;
        const d = unitDir(a, b);
        pickedSide = (camera.position.x - mx) * -d.dz + (camera.position.z - mz) * d.dx >= 0 ? 1 : -1;
      }
      if (spec.side) pickedSide = spec.side;
    }
  }

  function solve(spec: CineCamera, shotT: number, duration: number | undefined, dt: number): void {
    const D = SHOT_DEFAULTS;
    switch (spec.shot) {
      case "birdseye": {
        point(spec.target, p);
        const h = spec.height ?? D.birdseye.height;
        driftT += dt;
        desiredPos.set(p.x + (spec.drift ?? D.birdseye.drift) * driftT, p.y + h, p.z + h * D.birdseye.poleOffset);
        desiredTarget.set(p.x, p.y + 1, p.z);
        break;
      }
      case "wide": {
        point(spec.target, p);
        const dist = spec.dist ?? D.wide.dist;
        const az = spec.azimuth ?? D.wide.azimuth;
        desiredPos.set(p.x + Math.sin(az) * dist, p.y + (spec.height ?? D.wide.height), p.z + Math.cos(az) * dist);
        desiredTarget.set(p.x, p.y + D.lookY, p.z);
        break;
      }
      case "twoShot": {
        const a = res.actor(spec.a);
        const b = res.actor(spec.b);
        if (!a || !b) break;
        p.set((a.x + b.x) / 2, (a.y + b.y) / 2, (a.z + b.z) / 2);
        const d = unitDir(a, b);
        const sideDist = d.sep * D.twoShot.sepMul + D.twoShot.base + (spec.dist ?? 0);
        desiredPos.set(p.x + -d.dz * pickedSide * sideDist, p.y + D.twoShot.height, p.z + d.dx * pickedSide * sideDist);
        desiredTarget.set(p.x, p.y + D.twoShot.lookY, p.z);
        break;
      }
      case "overShoulder": {
        const from = res.actor(spec.from);
        const to = res.actor(spec.to);
        if (!from || !to) break;
        const d = unitDir(from, to);
        const o = D.overShoulder;
        desiredPos.set(
          from.x - d.dx * o.back + -d.dz * pickedSide * o.side,
          from.y + o.eye,
          from.z - d.dz * o.back + d.dx * pickedSide * o.side,
        );
        desiredTarget.set(to.x, to.y + D.faceY, to.z);
        break;
      }
      case "closeUp": {
        const a = res.actor(spec.target);
        if (!a) break;
        const ang = a.ry + (spec.azimuth ?? D.closeUp.azimuth);
        desiredPos.set(a.x + Math.sin(ang) * D.closeUp.ring, a.y + D.closeUp.camY, a.z + Math.cos(ang) * D.closeUp.ring);
        desiredTarget.set(a.x, a.y + (spec.height ?? D.faceY), a.z);
        break;
      }
      case "follow": {
        const single = point(spec.target, p);
        // Travel direction from the anchor's own motion. A stopped subject
        // KEEPS the last real direction (the camera must not swing around a
        // pair that pauses to talk); only a never-moved subject falls back to
        // its facing.
        if (havePrev) {
          q.copy(p).sub(prevPoint);
          q.y = 0;
        } else q.set(0, 0, 0);
        if (havePrev && q.lengthSq() >= (0.5 * dt) ** 2) {
          q.normalize();
          lastDir.copy(q);
          haveDir = true;
        } else if (haveDir) {
          q.copy(lastDir);
        } else {
          const ry = single?.ry ?? 0;
          q.set(Math.sin(ry), 0, Math.cos(ry));
        }
        prevPoint.copy(p);
        havePrev = true;
        const dist = spec.dist ?? D.follow.dist;
        const az = spec.azimuth ?? D.follow.azimuth;
        q.applyAxisAngle(UP, az);
        desiredPos.set(p.x - q.x * dist, p.y + (spec.height ?? D.follow.height), p.z - q.z * dist);
        desiredTarget.set(p.x, p.y + D.lookY, p.z);
        break;
      }
      case "orbit": {
        point(spec.target, p);
        orbitAngle += ((spec.degPerSec ?? D.orbit.degPerSec) * Math.PI / 180) * dt;
        const dist = spec.dist ?? D.orbit.dist;
        desiredPos.set(p.x + Math.sin(orbitAngle) * dist, p.y + (spec.height ?? D.orbit.height), p.z + Math.cos(orbitAngle) * dist);
        desiredTarget.set(p.x, p.y + D.lookY, p.z);
        break;
      }
      case "path": {
        if (!curve) break;
        const u = duration ? Math.min(1, Math.max(0, shotT / duration)) : 0;
        curve.getPointAt(u, desiredPos);
        point(spec.lookAt, q);
        if (typeof spec.lookAt === "string" || (Array.isArray(spec.lookAt) && typeof spec.lookAt[0] === "string")) {
          q.y += SHOT_DEFAULTS.lookY; // actor refs aim at the person, not the feet
        }
        desiredTarget.copy(q);
        break;
      }
    }
  }

  function apply(spec: CineCamera, shotT: number, duration: number | undefined, dt: number, cut: boolean): void {
    if (spec !== lastSpec) enterShot(spec);
    solve(spec, shotT, duration, dt);
    if (cut) {
      camPos.copy(desiredPos);
      camTarget.copy(desiredTarget);
    } else {
      const easeTime = spec.easeTime ?? SHOT_DEFAULTS.easeTime;
      const k = Math.min(1, (dt * 1.4) / easeTime); // 0.35 ⇒ the talk-camera dt·4
      camPos.lerp(desiredPos, k);
      camTarget.lerp(desiredTarget, k);
    }
    camera.position.copy(camPos);
    camera.lookAt(camTarget);
  }

  return { apply };
}
